import { useCallback, useState } from 'react'
import { z } from 'zod'
import { useFormValidation } from './useFormValidation'

// Payment form validation schema
export const paymentFormSchema = z.object({
	amount: z.number().min(0, 'Amount must be at least 0').max(10000, 'Amount cannot exceed 10,000 kr'),
	poiId: z.string().min(1, 'Please select a payment terminal'),
})

export type PaymentFormData = z.infer<typeof paymentFormSchema>

export interface UsePaymentFormOptions {
	initialValues?: Partial<PaymentFormData>
	onSubmit?: (data: PaymentFormData) => void | Promise<void>
}

export interface UsePaymentFormReturn {
	values: PaymentFormData
	errors: Record<string, string>
	isValid: boolean
	isSubmitting: boolean
	updateField: <K extends keyof PaymentFormData>(field: K, value: PaymentFormData[K]) => void
	handleSubmit: (e?: React.FormEvent) => Promise<void>
	reset: () => void
	setFieldError: (field: keyof PaymentFormData, message: string) => void
	clearFieldError: (field: keyof PaymentFormData) => void
}

export function usePaymentForm({
	initialValues = {},
	onSubmit,
}: UsePaymentFormOptions = {}): UsePaymentFormReturn {
	const [values, setValues] = useState<PaymentFormData>({
		amount: 0,
		poiId: '',
		...initialValues,
	})

	const [isSubmitting, setIsSubmitting] = useState(false)

	const { errors, isValid, validate, validateField, clearFieldError, setFieldError, clearErrors } =
		useFormValidation({
			schema: paymentFormSchema,
			validateOnChange: true,
		})

	const updateField = useCallback(
		<K extends keyof PaymentFormData>(field: K, value: PaymentFormData[K]) => {
			setValues((prev) => {
				const newValues = { ...prev, [field]: value }
				// Validate the field after updating
				validateField(field, value)
				return newValues
			})
		},
		[validateField]
	)

	const handleSubmit = useCallback(
		async (e?: React.FormEvent) => {
			e?.preventDefault()

			if (isSubmitting) return

			const isFormValid = validate(values)

			if (!isFormValid) {
				return
			}

			if (onSubmit) {
				setIsSubmitting(true)
				try {
					await onSubmit(values)
				} catch (error) {
					// Handle submission errors
					if (error instanceof Error) {
						setFieldError('poiId', error.message)
					}
				} finally {
					setIsSubmitting(false)
				}
			}
		},
		[values, validate, onSubmit, isSubmitting, setFieldError]
	)

	const reset = useCallback(() => {
		setValues({
			amount: 0,
			poiId: '',
			...initialValues,
		})
		clearErrors()
		setIsSubmitting(false)
	}, [initialValues, clearErrors])

	return {
		values,
		errors,
		isValid,
		isSubmitting,
		updateField,
		handleSubmit,
		reset,
		setFieldError,
		clearFieldError,
	}
}
